import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appToast } from "@components/Toast";
import { track } from "@lib/tracker";
import { useOrder } from "@utils/store/cart/useOrder";
import { useUser } from "@utils/store/user/useUser";
import { OA_ENTRY_PREFERRED_CONTEXT_KEY } from "@utils/line/oaEntryCheck";
import {
  OA_REORDER_EDIT_GONE_TEXT,
  decideOaEditDraft,
  findDraftAddress,
  parseOaEditDraftId,
  resolveOaEditDeliveryType,
  stripOaEditDraftParam,
} from "./oaReorderEdit";

const preferredContextPending = (): boolean => {
  try {
    return sessionStorage.getItem(OA_ENTRY_PREFERRED_CONTEXT_KEY) != null;
  } catch {
    return false;
  }
};

/**
 * 再叫一桶確認卡「我要修改」（`/kiosk?…&oaDraft=<draft_id>`）的進場處理，掛在 KioskHome。
 * 沒帶 oaDraft（一般從 rich menu / 分頁進 /kiosk）時完全不做事。
 *
 * 草稿本身由 PageWrapper 的 useCurrentDraftSync 載入，這裡只等它與 kiosk bootstrap 都載完：
 * - current draft 就是卡上那張 → 對齊地址與配送方式、轉 /kiosk/cart（replace，購物車的返回鍵本來就回 /kiosk）
 * - 不是（已送出／刪舊建新／過期／別的 context）→ 留在 /kiosk、拿掉 oaDraft、提示失效
 * 只處理一次；處理完把 oaDraft 從網址拿掉，重新整理或返回不會再觸發。
 *
 * 必須在 useKioskBootstrap 之後呼叫：同一元件的 effect 依宣告順序執行。
 */
export function useOaReorderEditEntry({ bootstrapLoaded, isOpenNow }: { bootstrapLoaded: boolean; isOpenNow: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { chooseIdentity, setChooseIdentity } = useUser();
  const { orderState, draftOrderId, productList, draft, setDeliveryTime } = useOrder();

  const expectedDraftId = parseOaEditDraftId(location.search);
  const contextId = chooseIdentity?.context_id ?? null;

  // 「看過這個 context 的 loading → success」才算草稿載完（見 OaEditDraftState.draftSettled）。
  const loadingSeenFor = useRef<{ contextId: number | string | null } | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (!expectedDraftId || handled.current) return;
    if (orderState === "loading") {
      loadingSeenFor.current = { contextId };
      return;
    }
    // 另外兩道防線：
    // - 還沒有身分（contextId null）不算數：OA 的 draft 綁 context，身分選單套用前抓到的不準。
    // - 切身分時舊 context 的請求可能晚回來；draft 帶 context_id，對不上目前身分就當作還沒載完。
    const draftSettled =
      orderState === "success" &&
      contextId != null &&
      loadingSeenFor.current !== null &&
      loadingSeenFor.current.contextId === contextId &&
      (!draft || draft.context_id == null || String(draft.context_id) === String(contextId));

    const decision = decideOaEditDraft({
      expectedDraftId,
      bootstrapLoaded,
      draftSettled,
      currentDraftId: draftOrderId,
      itemCount: productList.length,
      preferredContextPending: preferredContextPending(),
    });
    if (decision === "wait") return;
    handled.current = true;

    if (decision === "open_cart") {
      const addr = findDraftAddress(chooseIdentity?.address, draft?.customer_address_id);
      if (chooseIdentity && addr && String(chooseIdentity.choose_address?.customer_address_id) !== String(addr.customer_address_id)) {
        setChooseIdentity({ ...chooseIdentity, choose_address: addr });
      }
      const type = resolveOaEditDeliveryType(draft?.delivery_mode, isOpenNow);
      if (type) setDeliveryTime({ type, date: null, slotId: null, slotSource: null, slotLabel: null });
      track("entry_click", { entry_source: "oa_reorder_edit" });
      navigate("/kiosk/cart", { replace: true });
      return;
    }

    track("entry_click", { entry_source: "oa_reorder_edit_gone" });
    appToast.error(OA_REORDER_EDIT_GONE_TEXT);
    navigate({ pathname: location.pathname, search: stripOaEditDraftParam(location.search) }, { replace: true });
    // chooseIdentity（整個物件）也要在 deps：身分選單套用偏好身分時會先清 sessionStorage 再 setChooseIdentity，
    // 偏好的就是目前這個 context 時 context_id 不變、草稿也不重抓，靠這次 re-run 才會讀到「偏好已套用」。
  }, [expectedDraftId, orderState, contextId, chooseIdentity, bootstrapLoaded, draftOrderId, productList.length, draft, isOpenNow]);
}
